import { useMemo, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "react-query";
import { useTranslation } from 'react-i18next';
import { add, differenceInSeconds } from 'date-fns';
import i18n from "i18next";
import { weightGValue, formatDate, formatSumWithCurrency, formatNumber } from "@utils/formatValues";
import { useProfile } from "@contexts/ProfileContext";
import Skeleton from "@components/new/Skeleton";
import { Alert, AlertType } from "@components/new/Alert";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import { H4, Title2, SubTitle3 } from "@components/new/Typo";
import Photo from "@components/new/Photo";
import { Marketplace } from "@components/new/Marketplace";
import { Modal, ModalSize } from "@components/new/Modal";
import { Table, TableCellLink, TableColumn, TableRowType } from "@components/new/Table";
import { ButtonType } from "@components/new/Button";
import {
  getFBPRequestSKULabel,
  getFBPRequestLabel,
  getFBPRequestDetails,
  getProductLabel,
  getFBPPickupTariff,
  cancelFBPRestockRequest,
  isWbItem,
  isYmItem,
  isAliItem,
  getAliProductUrl,
  TRequestItemKnownExtended,
} from "@api/apiClient/fbp";
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import ConfirmModal from '@components/modals/ConfirmModal';
import StatusBadge from "../../components/StatusBadge";
import PickUpStatusBadge from "../../components/PickUpStatusBadge";
import DownloadIcon from '@assets/icons/outline/download.svg?react';
import CalendarClockIcon from '@assets/icons/outline/calendar-clock.svg?react';
import MapIcon from '@assets/icons/outline/map-pin.svg?react';
import UserIcon from '@assets/icons/outline/user.svg?react';
import TruckIcon from '@assets/icons/outline/truck.svg?react';
import LinkIcon from "@assets/icons/outline/square-arrow-out-up-right.svg?react";
import styles from "./Form.module.scss";

interface OrderModalProps {
  onClose: () => void;
  sellerWarehouseId: string;
  fbpWarehouseId?: string;
  isPickUpAvailable?: boolean;
  warehouseAddress: string;
  warehousePhone: string;
  id: string;
  createPickUp?: (v: string) => void;
}

type TRow = TRequestItemKnownExtended & {
  count?: number,
  marketplace: string;
};

const OrderModal = ({ onClose, sellerWarehouseId, fbpWarehouseId, isPickUpAvailable: warehousePickUpAvailable, id, warehouseAddress, warehousePhone, createPickUp }: OrderModalProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { account } = useProfile();
  // Feature FreeFbpPickUp: пикап бесплатный — не показываем стоимость тарифа.
  const isFreeFbpPickUp = Boolean(account?.features?.freeFbpPickUp);
  const currentLanguage = i18n.language;
  const [isOpenCancelWindow, setIsOpenCancelWindow] = useState<boolean>(false);

  const downloadProductLabelsMutation = useMutation({
    mutationFn: (id: string) => getProductLabel({ sellerWarehouseId, id }),
    onSuccess: (response) => {
      const file = new Blob([response], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      const newWindow = window.open(fileURL);
      newWindow!.onload = () => {
        newWindow!.print();
      };
    },
  });

  const cancelRequestMutation = useMutation({
    mutationFn: (id: string) => cancelFBPRestockRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries(
        { queryKey: ["getFBPRequests"] },
      );
      queryClient.invalidateQueries(
        { queryKey: ["getFBPRequestDetails", id] },
      );
    },
  });

  const downloadRequestLabelsMutation = useMutation({
    mutationFn: (id: string) => getFBPRequestLabel({ id }),
    onSuccess: ({ data, filename }) => {
      const file = new Blob([data], { type: 'application/zip' });
      const fileURL = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = fileURL;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    },
  });

  const downloadRequestSKULabelsMutation = useMutation({
    mutationFn: (id: string) => getFBPRequestSKULabel({ id }),
    onSuccess: ({ data, filename }) => {
      const file = new Blob([data], { type: 'application/zip' });
      const fileURL = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = fileURL;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    },
  });

  const { data: tariff } = useQuery({
    queryKey: ['getFBPPickupTariff', fbpWarehouseId],
    queryFn: () => getFBPPickupTariff(fbpWarehouseId ?? ""),
    retry: 1,
    enabled: Boolean(fbpWarehouseId) && !isFreeFbpPickUp,
  });

  const requestQuery = useQuery({
    queryKey: ["getFBPRequestDetails", id],
    queryFn: () => getFBPRequestDetails(id),
    retry: 1,
    select: (data) => {
      const items = [
        ...data.ymItems.map(v => ({
          ...v.ymProduct,
          marketplace: "Yandex",
          count: v.count,
        })),
        ...data.wbItems.map(v => ({
          ...v.wbProduct,
          marketplace: "Wildberries",
          count: v.count,
        })),
        ...(data.aliItems ?? []).map(v => ({
          ...v.aliProduct,
          marketplace: "Aliexpress",
          url: getAliProductUrl(v.aliProduct.aliItemId),
          count: v.count,
        })),
      ];
      return {
        ...data,
        items: items,
        weight: items.reduce((acc, v) => ((v.weight
          ? (weightGValue(v.weight) * v.count)
          : 0
        ) + acc), 0) / 1000,
        count: items.length,
      };
    }
  });

  const columns: TableColumn<TRow>[] = useMemo(() => [
    {
      header: t("fbpPage.createOrder.items.photo"),
      renderCell: (row) => (
        <Photo src={row.data.primaryImage} />
      ),
      width: "80px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.createOrder.items.marketplace"),
      renderCell: (row) => (
        <Marketplace name={row.data.marketplace} size="small" />
      ),
      alignment: "center",
      width: "40px",
      fixWidth: true,
    },
    {
      header: t("fbpPage.createOrder.items.name"),
      renderCell: (row) => {
        const description = isYmItem(row.data) ? row.data.sku : (
          isWbItem(row.data) ? row.data.wbNmId : (
            isAliItem(row.data) ? row.data.skuCode : ""
          )
        );
        return (
          <TableCellLink
            title={row.data.name}
            description={description}
            to={row.data.url}
            external
          />
        )
      },
      width: "200px",
    },
    {
      header: t("fbpPage.createOrder.items.size"),
      valueGetter: (row) => ((isWbItem(row) && row.wbTechSize) || ""),
      width: "160px",
      fixWidth: true,
      alignment: "center",
    },
    {
      header: t("fbpPage.createOrder.items.count"),
      valueGetter: (data: TRow) => formatNumber(data.count ?? 0),
      width: "100px",
      fixWidth: true,
      alignment: "center",
    },
    ...((requestQuery.data && ["Accepted", "WarehouseReceiving", "OnWay"]
        .find(v => (v === requestQuery.data.status))) ? [
      {
        header: t("fbpPage.createOrder.items.barcode"),
        renderCell: (row: TableRowType<TRow>) => (
          <IconButton
            icon={DownloadIcon}
            onClick={() => downloadProductLabelsMutation.mutate(row.data.id)}
            size={IconButtonSize.S}
            type={IconButtonType.SECONDARY}
          />
        ),
        width: "80px",
        fixWidth: true,
        alignment: "center",
      },
    ] : []) as TableColumn<TRow>[]
  ], [currentLanguage, requestQuery.data]);

  const endDate = requestQuery.data?.createdAt ? add(requestQuery.data?.createdAt, { hours: 1 }) : "";
  const timeout = endDate ? differenceInSeconds(new Date(endDate), new Date()) : 0;

  const skeletonColumns: TableColumn<null>[] = useMemo(() => columns.map(v => ({
    ...v,
    renderCell: undefined,
    valueGetter: undefined,
  })), [columns])

  const totalWeight = requestQuery.data?.weight ?? 0;
  const isPickUpAvailable = warehousePickUpAvailable && createPickUp && requestQuery.data && !requestQuery.data?.shipmentDate
    && requestQuery.data.isPickUpAvailable
    && (totalWeight >= 10) && (totalWeight <= 3000)
    && ["Accepted", "Created", "OnWay"].find(v => (v === requestQuery.data?.status));

  return (
    <>
      <Modal
        isOpen={true}
        title={(<H4>{requestQuery.data?.number ?? ""}</H4>)}
        size={ModalSize.L}
        onClose={onClose}
        headerContent={requestQuery.data?.deliveryTrackNumber ? (
          <div className={styles.trackingNumberView}>
            <SubTitle3 color="grey-700">{t("fbpPage.pickUp.trackingNumberTitle")}</SubTitle3>
            <div className={styles.trackingNumberRow}>
              <CopyTextToClipboard text={requestQuery.data?.deliveryTrackNumber ?? ""}>
                <Title2>{requestQuery.data?.deliveryTrackNumber ?? ""}</Title2>
              </CopyTextToClipboard>
              <LinkButton
                size={LinkButtonSize.L}
                title={t("fbpPage.pickUp.deliveryLink")}
                rightIcon={LinkIcon}
                to={"https://www.ky-express.com/88.html#tabPoint"}
                external
              />
            </div>
          </div>
        ) : null}
        footerButtons={[
          ...((requestQuery.data && requestQuery.data?.isCancelable) ? [(
            {
              onClick: () => setIsOpenCancelWindow(true),
              loading: cancelRequestMutation.isLoading,
              title: t("fbpPage.ordersTab.cancelBtn"),
              timeout: timeout > 0 ? timeout : 0,
              startTimerFromOutside: timeout > 0 ? true : undefined,
              type: ButtonType.SECONDARY,
            }
          )] : []),
          ...((requestQuery.data &&
            ["Accepted", "WarehouseReceiving", "PartiallyCompleted", "OnWay"]
              .find(v => (v === requestQuery.data.status))) ? ([
            {
              type: ButtonType.PRIMARY,
              icon: DownloadIcon,
              onClick: () => downloadRequestSKULabelsMutation.mutate(id),
              loading: downloadRequestSKULabelsMutation.isLoading,
              title: t("fbpPage.createOrder.labelsSKUBtn"),
            },
            {
              type: ButtonType.PRIMARY,
              icon: DownloadIcon,
              onClick: () => downloadRequestLabelsMutation.mutate(id),
              loading: downloadRequestLabelsMutation.isLoading,
              title: t("fbpPage.createOrder.labelsBtn"),
            },
          ]) : []),
        ]}
      >
        {requestQuery.data?.status === "Rejected" ? (
          <Alert
            type={AlertType.WARNING}
            title={t("fbpPage.problematicAlert", { address: warehouseAddress, phone: warehousePhone })}
            isWide
          />
        ) : (isPickUpAvailable ? (
          <Alert
            type={AlertType.NEUTRAL}
            title={isFreeFbpPickUp
              ? t("fbpPage.pickUp.alertTitleFree")
              : t("fbpPage.pickUp.alertTitle", {
                price: formatSumWithCurrency(tariff?.pricePerKg ?? 0, tariff?.currency ?? "CNY"),
              })}
            description={t("fbpPage.pickUp.alertDescription")}
            button={{
              title: t("fbpPage.createOrder.completion.successBtn"),
              onClick: () => createPickUp(id),
              suffix: TruckIcon,
            }}
          />
        ) : null)}

        <div className={styles.sectionRow}>
          <div className={styles.section}>
            <h4>{t('fbpPage.createOrder.params.title')}</h4>

            {requestQuery.isLoading ? (
              <div className={styles.params}>
                <Skeleton height="36px" width="80px" />
                <Skeleton height="36px" width="80px" />
                <Skeleton height="36px" width="80px" />
                <Skeleton height="36px" width="80px" />
                <Skeleton height="36px" width="80px" />
              </div>
            ) : (
              <div className={styles.params}>
                <div className={styles.paramsItem}>
                  <SubTitle3 color="grey-700">{t('fbpPage.createOrder.params.count')}</SubTitle3>
                  <Title2>{requestQuery.data?.count}</Title2>
                </div>

                <div className={styles.paramsItem}>
                  <SubTitle3 color="grey-700">{t('fbpPage.createOrder.params.weight')}</SubTitle3>
                  <Title2>{requestQuery.data?.weight ? `${formatNumber(requestQuery.data?.weight)} ${t('fbpPage.createOrder.params.kg')}` : ""} </Title2>
                </div>

                <div className={styles.paramsItem}>
                  <SubTitle3 color="grey-700">{t('fbpPage.createOrder.params.createdAt')}</SubTitle3>
                  <Title2>{formatDate(requestQuery.data?.createdAt, 'dd.MM.yy')}</Title2>
                </div>

                <div className={styles.paramsItem}>
                  <SubTitle3 color="grey-700">{t('fbpPage.createOrder.params.status')}</SubTitle3>
                  <StatusBadge value={requestQuery.data?.status} />
                </div>

                <div className={styles.paramsItem}>
                  <SubTitle3 color="grey-700">{t('fbpPage.createOrder.params.boxCount')}</SubTitle3>
                  <Title2>{requestQuery.data?.boxCount}</Title2>
                </div>
              </div>
            )}
          </div>

          {warehousePickUpAvailable && requestQuery.data?.shipmentDate ? (
            <div className={styles.section}>
              <div className={styles.pickUpDetailsRow}>
                <h4>{t('fbpPage.pickUp.pickUpData')}</h4>
                <PickUpStatusBadge value={requestQuery.data?.pickUpStatus} />
              </div>
              {requestQuery.isLoading ? (
                <div className={styles.pickUpData}>
                  <Skeleton height="12px" width="400px" />
                  <Skeleton height="12px" width="400px" />
                  <Skeleton height="12px" width="400px" />
                </div>
              ) : (
                <div className={styles.pickUpData}>
                  <div className={styles.pickUpDataItem}>
                    <CalendarClockIcon />
                    <Title2>{formatDate(requestQuery.data?.shipmentDate, 'dd/MM/yyyy')}</Title2>
                  </div>

                  <div className={styles.pickUpDataItem}>
                    <MapIcon />
                    <Title2>{requestQuery.data?.addressRawLine}</Title2>
                  </div>

                  <div className={styles.pickUpDataItem}>
                    <UserIcon />
                    <Title2>{[requestQuery.data?.contactName, requestQuery.data?.shipmentTime].join("; ")}</Title2>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className={styles.section2}>
          <h4>{t('fbpPage.createOrder.items.title')}</h4>

          {requestQuery.isLoading ? (
            <Table<null>
              isSkeleton
              columns={skeletonColumns}
              data={[null, null, null, null]}
            />
          ) : (
            <Table<TRow>
              columns={columns}
              data={requestQuery.data?.items ?? []}
            />
          )}
        </div>
      </Modal>

      {isOpenCancelWindow && (
        <ConfirmModal
          key={id}
          title={requestQuery.data?.shipmentDate
            ? t("fbpPage.cancelRequestModal.pickUpTitle")
            : t("fbpPage.cancelRequestModal.title")}
          description={requestQuery.data?.shipmentDate
            ? t("fbpPage.cancelRequestModal.pickUpDescription")
            : t("fbpPage.cancelRequestModal.description", { number: requestQuery.data?.number })}
          noBtnTitle={t("fbpPage.cancelRequestModal.cancelBtn")}
          yesBtnTitle={t("fbpPage.cancelRequestModal.okBtn")}
          onClose={() => setIsOpenCancelWindow(false)}
          requestFn={() => cancelRequestMutation.mutate(id ?? "")}
          onSuccess={() => setIsOpenCancelWindow(false)}
        />
      )}
    </>
  )
}

export default OrderModal;
